import { LocationType, MenuCategory } from '@prisma/client';

/**
 * Contrato do cardápio público (GET /public/menu/:slug).
 *
 * ATENÇÃO: estes tipos são projeções deliberadamente estreitas, NÃO `Partial<Prisma...>`.
 * O payload público não pode carregar `cost`, `sku`, `barcode`, `companyId`, `printerId`
 * nem qualquer id interno além do `productId`, que o envio do pedido precisa.
 * Trocar por `Partial<Product>` reabre o vazamento em silêncio.
 */
export interface PublicMenuOption {
  id: string;
  name: string;
  order: number;
  /** M2-D82 — grupo da opção (`optionGroups[].id`), ou `null` na opção livre. */
  groupId: string | null;
  /**
   * M2-D82 — preço do adicional no ponto de venda do QR, em reais; `0` é grátis. Já passou pela guarda de
   * leitura: é o mesmo valor que o pedido cobra.
   */
  price: number;
}

/**
 * M2-D82 — grupo de opções do produto, já passado pela guarda de leitura (M2-D87): grupo sem opção não
 * chega, e `min` é o **efetivo** (o menor entre o cadastrado e as opções que o grupo tem). É o mesmo
 * `min` que o pedido confere, então a folha de opções nunca libera o que o servidor vai recusar.
 *
 * Seleção única é `max: 1` (M2-D74): a tela deriva rádio ou caixa de seleção daqui, não de um campo de
 * modo.
 */
export interface PublicMenuOptionGroup {
  id: string;
  name: string;
  order: number;
  min: number;
  max: number;
}

export interface PublicMenuProduct {
  id: string;
  name: string;
  detail?: string;
  image?: string;
  /** Preço do ProductPrice do ponto de venda resolvido pelo slug — nunca o Product.price */
  price: number;
  /** Na ordem do snapshot do pedido: agrupadas na ordem dos grupos, as sem grupo no fim (M2-D78). */
  options: PublicMenuOption[];
  /** M2-D82 — só os grupos com opção aberta, com o `min` efetivo. `[]` no produto sem grupo. */
  optionGroups: PublicMenuOptionGroup[];
}

/**
 * Id do grupo SINTÉTICO que agrega produtos sem grupo (ou de grupo inativo/inexistente).
 * Compartilhado entre o backend (que monta o grupo) e o frontend (que o traduz) para a
 * magic string não divergir entre as pontas.
 */
export const UNGROUPED_GROUP_ID = 'ungrouped';

export interface PublicMenuGroup {
  id: string;
  /**
   * Nome cadastrado pelo gerente (não traduzível). Exceção: quando `id` é
   * `UNGROUPED_GROUP_ID`, o name é um placeholder do servidor — o cliente DEVE exibir a
   * própria tradução (o frontend usa a chave `menu.catalog.ungrouped-group`), nunca o cru.
   */
  name: string;
  menuCategory?: MenuCategory;
  /**
   * Id de símbolo do sprite `material-outline.svg` (valores do enum `MenuGroupIcon`), usado na
   * navegação do cardápio. Ausente quando o gerente não escolheu — o cliente cai no ícone padrão
   * da categoria (`DEFAULT_MENU_GROUP_ICON`).
   */
  icon?: string;
  /**
   * Descrição do grupo, cadastrada pelo gerente e exibida abaixo do título da seção. Substituiu a
   * contagem de itens, que ocupava a linha sem informar nada. Ausente quando não foi preenchida —
   * o cliente então não renderiza a linha (não inventa texto no lugar).
   */
  detail?: string;
  products: PublicMenuProduct[];
}

export interface PublicMenuCompany {
  name: string;
  logo?: string;
  textColor?: string;
  backgroundColor?: string;
  /**
   * M2-D60 — a empresa ligou o chamado de garçom (`WAITER_CALL_ENABLED`). Só a metade da regra: o botão
   * aparece quando isto **e** `canCallWaiter(location.type)` valem. É prévia — o `POST` relê a cfg.
   *
   * Mora no bloco `company` porque ele entra inteiro no cache do cardápio, sem cópia campo a campo.
   */
  waiterCall: boolean;
}

export interface PublicMenuPointOfSale {
  name: string;
  isOpen: boolean;
  /** Próximo horário de abertura, para a tela de "fechado" dizer quando volta */
  opensAt?: string;
}

export interface PublicMenuLocation {
  type: LocationType;
  number: string;
}

/** Teto de destaques por ponto de venda. Compartilhado: servidor valida, cliente bloqueia o 5º. */
export const MAX_FEATURED_PRODUCTS = 4;

/**
 * Teto de faixas na grade de horários de um ponto de venda.
 *
 * Compartilhado entre o DTO que valida e o guard que compara com o OpenAPI: uma grade real tem
 * poucas faixas por dia, e o campo é embutido — TODA avaliação de "está aberto?" o percorre,
 * inclusive a do cardápio público.
 */
export const MAX_OPENING_HOURS = 50;

/**
 * Faixa de preço de produto, em reais.
 *
 * 🔴 O piso **não é formalidade**: preço zero abre abuso pelo cardápio — item de cortesia é pedível
 * sem limite e o custo é comida produzida (decisão de 2026-07-29). A regra vale em quatro escritores
 * (cadastro, upsert da integração, cardápio e pedido) porque DTO não valida o que já foi gravado;
 * a constante vive aqui para que os quatro citem o mesmo número, e não quatro literais que divergem
 * no primeiro ajuste.
 */
export const MIN_PRODUCT_PRICE = 0.01;
export const MAX_PRODUCT_PRICE = 9999999.99;

export interface PublicMenu {
  company: PublicMenuCompany;
  pointOfSale: PublicMenuPointOfSale;
  location: PublicMenuLocation;
  /**
   * Produtos em destaque no ponto de venda, exibidos ACIMA de tudo, em card próprio (até
   * `MAX_FEATURED_PRODUCTS`).
   *
   * NÃO é um grupo e **não entra na navegação**: não é selecionável. Vem como lista à parte, e não
   * como uma terceira categoria, justamente para o cliente não conseguir renderizá-lo como aba por
   * engano. Cada produto daqui **continua aparecendo na seção do grupo dele** — destaque é atalho,
   * não mudança de lugar; sumir do grupo esconderia o item de quem navega.
   */
  featured: PublicMenuProduct[];
  groups: PublicMenuGroup[];
}
